import React, { useCallback, useState } from 'react';
import { getDownloadURL, getMetadata, ref } from 'firebase/storage';
import { adminApi } from '../../config/axios';
import { storage, authReady } from '../../config/firebase';
import { fetchDeviceLogFile, logObjectPath } from '../../utils/deviceLogFile';

/** Admin-only: ask the player to upload its log file, wait for it, then open the download. (The customer page no longer offers this.) */
export default function DeviceLogDownload({ deviceId, serial }) {
  const [state, setState] = useState('idle'); // idle | sending | waiting | timeout | error
  const run = useCallback(async () => {
    if (!serial || state === 'sending' || state === 'waiting') return;
    setState('sending');
    try {
      await authReady; // Storage rules gate logs/* on auth != null
      const objectRef = ref(storage, logObjectPath(serial));
      const result = await fetchDeviceLogFile({
        getMeta: () => getMetadata(objectRef),
        getUrl: () => getDownloadURL(objectRef),
        sendCommand: () => adminApi.post(`/device/${deviceId}/command`, { command: 'uploadlogs' }),
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        onWaiting: () => setState('waiting'),
      });
      if (result.status === 'ok') {
        window.open(result.url, '_blank', 'noopener');
        setState('idle');
      } else {
        setState('timeout');
      }
    } catch (_e) {
      setState('error');
    }
  }, [deviceId, serial, state]);

  const busy = state === 'sending' || state === 'waiting';
  return (
    <div className="flex flex-col gap-1" data-testid="device-log-download">
      <button onClick={run} disabled={busy} className="btn-secondary text-xs" title="Ask the player to upload its log file, then download it">
        {busy ? '⏳ Waiting for the device…' : '⬇ Download device log file'}
      </button>
      {state === 'waiting' && <span className="text-[11px] text-text-muted">The device was asked to upload its log. This can take up to 2 minutes.</span>}
      {state === 'timeout' && <span className="text-[11px] text-danger" data-testid="log-timeout">The device did not upload a new log file within 2 minutes (it may be offline). Try again.</span>}
      {state === 'error' && <span className="text-[11px] text-danger" data-testid="log-error">Could not request the log file. Check the device is online and try again.</span>}
    </div>
  );
}
